import type { z } from 'zod';

import type { KeyedRequired, LedgerAccountType } from '@fragment/types';
import type {
  CurrencyMatchInputSchema,
  CurrencyMode,
  CurrencyModeSchema,
} from '@fragment/types/currencyCodes';
import type {
  AccountConsistencyConfig,
  BaseChartOfAccounts,
  BaseSchemaLedgerAccountInputType,
} from '@fragment/types/schemas';
import type { SafeString } from '@fragment/types/strings';

import type { SchemaCurrencyMatchInput } from '../../types';
import { getSchemaObjectParameters } from '../../parameterization';
import {
  AccountCategory,
  AccountCategoryMutualExclusionError,
  getSchemaAccountCategory,
} from '../accountCategory';
import {
  isTotalBalanceSchema,
  validateAccountConsistencySettings,
} from '../consistencyValidation';
import { validateDefaultCurrencySettings } from '../currencyValidation';
import { paymentAccountUniquenessMessage } from '../paymentAccountEligibility';
import {
  inferAccountName,
  resolveAccountInheritedFields,
} from '../resolveAccountInheritedFields';
import { getAccountStatus } from '../status';
import { validateAccount } from './account';

export type SelfChildren<T> = Omit<T, 'children'> & {
  children?: SelfChildren<T>[];
};

type FillInParams<AT extends BaseSchemaLedgerAccountInputType> = {
  defaultConsistencyConfig?: AccountConsistencyConfig;
  defaultCurrency?: z.infer<typeof CurrencyMatchInputSchema>;
  defaultCurrencyMode?: z.infer<typeof CurrencyModeSchema>;
  accounts: SelfChildren<AT>[];
};
type FillInResult<AT extends BaseSchemaLedgerAccountInputType> = KeyedRequired<
  FillInParams<AT>,
  'defaultConsistencyConfig' | 'defaultCurrencyMode'
>;

type AccountConsistencyConfigReturnType = Omit<
  Required<AccountConsistencyConfig>,
  'totalBalanceUpdates' | 'ownBalanceUpdates'
> & {
  totalBalanceUpdates?: AccountConsistencyConfig['totalBalanceUpdates'];
  ownBalanceUpdates?: AccountConsistencyConfig['ownBalanceUpdates'];
};

// TODO - the way we handle type has to be changed here.
// Types should all come from the root accounts, and be inherited by children.
// even the child has a different type we don't error, we just inherit the type from the parent.
// We need to consider making the types on the root accounts required as a matter of
// structural correctness.
export const fillInDefaultsAndInferMetadata = <
  AT extends BaseSchemaLedgerAccountInputType
>({
  defaultConsistencyConfig: defaultConsistencyConfigInput,
  defaultCurrency,
  defaultCurrencyMode: defaultCurrencyModeInput,
  accounts,
}: FillInParams<AT>): FillInResult<
  AT & {
    consistencyConfig: AccountConsistencyConfigReturnType;
    currencyMode: CurrencyMode;
    name: string;
    parameters: string[];
  }
> => {
  type AccountInputType = SelfChildren<AT>;
  type AccountReturnType = SelfChildren<
    AT & {
      consistencyConfig: AccountConsistencyConfigReturnType;
      currencyMode: CurrencyMode;
      name: string;
      parameters: string[];
    }
  >;

  const defaultCurrencyMode = defaultCurrencyModeInput ?? 'single';
  // Check if this is a totalBalance schema before defaulting ownBalanceUpdates
  const isTotalBalance = isTotalBalanceSchema(defaultConsistencyConfigInput);
  const defaultConsistencyConfig: Omit<
    Required<AccountConsistencyConfig>,
    'groups' | 'totalBalanceUpdates' | 'ownBalanceUpdates'
  > & {
    totalBalanceUpdates?: AccountConsistencyConfig['totalBalanceUpdates'];
    ownBalanceUpdates?: AccountConsistencyConfig['ownBalanceUpdates'];
  } = {
    lines: defaultConsistencyConfigInput?.lines ?? 'eventual',
    // Only include ownBalanceUpdates if NOT a totalBalance schema
    ...(!isTotalBalance && {
      ownBalanceUpdates:
        defaultConsistencyConfigInput?.ownBalanceUpdates ?? 'eventual',
    }),
    // Only include totalBalanceUpdates if it's defined
    ...(isTotalBalance && {
      totalBalanceUpdates: defaultConsistencyConfigInput?.totalBalanceUpdates,
    }),
  };
  /**
   * Resolves the currency, currency mode, consistency, and type of an account
   * through its parent chain, and stamps name, parameters, and status.
   */
  const resolveSchemaAccountInheritedFields = ({
    account,
    fallbackDefaultCurrencyMode,
    fallbackDefaultCurrency,
    fallbackDefaultConsistencyMode,
    parentAccountType,
    isTemplateChild = false,
  }: {
    account: AccountInputType;
    fallbackDefaultCurrencyMode: z.infer<typeof CurrencyModeSchema>;
    fallbackDefaultCurrency:
      | z.infer<typeof SchemaCurrencyMatchInput>
      | undefined;
    fallbackDefaultConsistencyMode: AccountConsistencyConfigReturnType;
    parentAccountType: LedgerAccountType | undefined;
    isTemplateChild?: boolean;
  }): AccountReturnType => {
    // TODO - handle custom currencies here
    // https://linear.app/fragment/issue/FRA-1869/make-visualizer-handle-custom-currencies

    const partialConsistencyConfig: AT['consistencyConfig'] =
      account.consistencyConfig ?? {};

    const {
      type,
      currency: newCurrency,
      currencyMode: newCurrencyMode,
      ...balanceUpdates
    } = resolveAccountInheritedFields<z.infer<typeof SchemaCurrencyMatchInput>>(
      {
        own: {
          type: account.type,
          currency: account.currency,
          currencyMode: account.currencyMode,
          ownBalanceUpdates: partialConsistencyConfig.ownBalanceUpdates,
          totalBalanceUpdates: partialConsistencyConfig.totalBalanceUpdates,
        },
        inherited: {
          type: parentAccountType,
          currency: fallbackDefaultCurrency,
          currencyMode: fallbackDefaultCurrencyMode,
          ownBalanceUpdates: fallbackDefaultConsistencyMode.ownBalanceUpdates,
        },
        isTotalBalance,
      }
    );

    const newConsistencyConfig: {
      ownBalanceUpdates?: 'strong' | 'eventual';
      totalBalanceUpdates?: 'strong' | 'eventual';
      lines: 'strong' | 'eventual';
      groups: {
        ownBalanceUpdates: 'strong' | 'eventual';
        key: SafeString;
      }[];
    } = {
      lines:
        partialConsistencyConfig.lines ?? fallbackDefaultConsistencyMode.lines,
      groups:
        partialConsistencyConfig.groups ??
        fallbackDefaultConsistencyMode.groups,
      ...balanceUpdates,
    };

    const newName = inferAccountName({
      key: account.key,
      name: account.name,
    });

    const status = getAccountStatus(account);
    const updatedAccount: AT & {
      consistencyConfig: AccountConsistencyConfigReturnType;
      currencyMode: 'single' | 'multi';
      children: undefined;
      name: string;
      parameters: string[];
    } = {
      ...(account as AT),
      children: undefined,
      type,
      parameters: getSchemaObjectParameters(account),
      consistencyConfig: newConsistencyConfig,
      name: newName,
      currency: newCurrency,
      currencyMode: newCurrencyMode,
      status,
    };

    return {
      ...updatedAccount,
      children: (account.children ?? []).map((child) =>
        resolveSchemaAccountInheritedFields({
          account: child,
          fallbackDefaultCurrencyMode: newCurrencyMode,
          fallbackDefaultCurrency: newCurrency,
          fallbackDefaultConsistencyMode: newConsistencyConfig,
          parentAccountType: updatedAccount.type,
          isTemplateChild: isTemplateChild || account.template,
        })
      ),
    };
  };

  return {
    defaultCurrencyMode,
    defaultCurrency,
    defaultConsistencyConfig,
    accounts: accounts.map((child) =>
      resolveSchemaAccountInheritedFields({
        account: child,
        fallbackDefaultCurrencyMode: defaultCurrencyMode,
        fallbackDefaultCurrency: defaultCurrency,
        fallbackDefaultConsistencyMode: {
          groups: [],
          ...defaultConsistencyConfig,
        },
        parentAccountType: undefined,
        isTemplateChild: false,
      })
    ),
  };
};
export const MAX_COA_DEPTH = 10;

/**
 * Validates that account-level consistency config matches the schema type.
 * - Total balance schemas cannot have ownBalanceUpdates set at account level
 * - Own balance schemas cannot have totalBalanceUpdates set at account level
 *
 * This walks through all accounts recursively to check for mismatches.
 */
const validateAccountConsistencyType = <
  AT extends BaseSchemaLedgerAccountInputType
>({
  accounts,
  isTotalBalance,
}: {
  accounts: SelfChildren<AT>[];
  isTotalBalance: boolean;
}): { message: string; path: (string | number)[] }[] => {
  const errors: { message: string; path: (string | number)[] }[] = [];

  const walkAccounts = (
    accountList: SelfChildren<AT>[],
    basePath: (string | number)[]
  ) => {
    accountList.forEach((account, idx) => {
      const accountPath = [...basePath, idx];

      // Check if account explicitly sets the wrong balance type
      if (
        isTotalBalance &&
        account.consistencyConfig?.ownBalanceUpdates !== undefined
      ) {
        errors.push({
          path: [...accountPath, 'consistencyConfig', 'ownBalanceUpdates'],
          message: `Account "${account.key}" has ownBalanceUpdates set, but schema uses totalBalance. Remove ownBalanceUpdates from account-level config.`,
        });
      }
      if (
        !isTotalBalance &&
        account.consistencyConfig?.totalBalanceUpdates !== undefined
      ) {
        errors.push({
          path: [...accountPath, 'consistencyConfig', 'totalBalanceUpdates'],
          message: `Account "${account.key}" has totalBalanceUpdates set, but schema uses ownBalance. Remove totalBalanceUpdates from account-level config.`,
        });
      }

      // Recursively check children
      if (account.children && account.children.length > 0) {
        walkAccounts(account.children, [...accountPath, 'children']);
      }
    });
  };

  walkAccounts(accounts, ['accounts']);
  return errors;
};

/**
 * we check for semantic errors in the accounts here
 * we don't need to check for structural errors with a zod
 * schema because the argument is already parsed by a base schema
 */
export const transformAndValidateAccounts = <
  AT extends BaseSchemaLedgerAccountInputType
>({
  accounts,
  defaultConsistencyConfig,
  defaultCurrency,
  defaultCurrencyMode,
}: FillInParams<AT>) => {
  const errors: {
    message: string;
    path: (string | number)[];
  }[] = [];

  const filledIn = fillInDefaultsAndInferMetadata({
    defaultConsistencyConfig,
    defaultCurrency,
    defaultCurrencyMode,
    accounts,
  });

  const ledgerConsistencyErrors = validateAccountConsistencySettings(
    filledIn.defaultConsistencyConfig
  );
  errors.push(...ledgerConsistencyErrors.errors);

  filledIn.accounts.forEach((account, idx) => {
    const accountValidationErrors = validateAccount({
      account,
      path: ['accounts', idx],
      isTemplateChild: account.template,
      defaultConsistencyConfig: filledIn.defaultConsistencyConfig,
    });
    errors.push(...accountValidationErrors);
  });

  // Validate account-level consistency config matches schema type (ownBalance vs totalBalance)
  const isTotalBalance = isTotalBalanceSchema(defaultConsistencyConfig);
  const accountConsistencyTypeErrors = validateAccountConsistencyType({
    accounts,
    isTotalBalance,
  });
  errors.push(...accountConsistencyTypeErrors);

  type WalkedNode = {
    account: FillInResult<AT>['accounts'][number];
    errorPath: (string | number)[];
    accountPath: string | undefined;
    depth: number;
  };
  const walk = (
    account: FillInResult<AT>['accounts'][number],
    errorPath: (string | number)[],
    depth: number,
    accountPath?: string
  ): WalkedNode[] => [
    { account, errorPath, accountPath, depth },
    ...(account.children ?? []).flatMap((child, idx) =>
      walk(
        child,
        [...errorPath, 'children', idx],
        depth + 1,
        `${accountPath}/${child.key}`
      )
    ),
  ];
  const walkedNodes = filledIn.accounts.flatMap((account, idx) =>
    walk(account, ['accounts', idx], 1, account.key)
  );

  walkedNodes
    .filter(({ depth }) => depth > MAX_COA_DEPTH)
    .forEach(({ account, errorPath, accountPath }) =>
      errors.push({
        message: `You attempted to add an account (key: ${account.key}), which exceeds the allowed maximum depth of 10 nested accounts. Path: ${accountPath}`,
        path: errorPath,
      })
    );

  // Check for duplicate account paths
  // This map is keyed by account path, and the value is an array of coaPaths.
  // if coaPath > 1, then we have a duplicate account path.
  const seenAccountPaths = new Map<string, (string | number)[][]>();
  walkedNodes.forEach(({ accountPath, errorPath }) => {
    if (accountPath) {
      seenAccountPaths.set(accountPath, [
        ...(seenAccountPaths.get(accountPath) ?? []),
        errorPath,
      ]);
    }
  });
  seenAccountPaths.forEach((coaPaths, accountPath) => {
    if (coaPaths.length > 1) {
      coaPaths.forEach((coaPath) =>
        errors.push({
          message: `Duplicate account paths: ${accountPath}`, // This is the same error message we previously used in schema-commands.
          path: coaPath,
        })
      );
    }
  });

  const paymentAccounts = walkedNodes.filter(({ account }) => {
    try {
      return getSchemaAccountCategory(account) === AccountCategory.Payment;
    } catch (e) {
      // validateAccountCategory already reports the multi category error
      if (e instanceof AccountCategoryMutualExclusionError) {
        return false;
      }
      throw e;
    }
  });
  if (paymentAccounts.length > 1) {
    paymentAccounts.forEach(({ account, errorPath }) =>
      errors.push({
        message: paymentAccountUniquenessMessage({ key: account.key }),
        path: [...errorPath, 'payment'],
      })
    );
  }
  // We don't return `path` and `errorPath` on the object because this code is shared with
  // the actual schemas code it messes with the backwards compatibility
  return { filledIn, errors };
};

export type ValidatedAccounts<AT extends BaseSchemaLedgerAccountInputType> =
  ReturnType<typeof transformAndValidateAccounts<AT>>['filledIn'];

export type ValidatedAccount<AT extends BaseSchemaLedgerAccountInputType> =
  ValidatedAccounts<AT>['accounts'][number];

type ValidateChartOfAccountsParams<
  CT extends z.infer<typeof BaseChartOfAccounts>
> = {
  coa: CT & { accounts: SelfChildren<CT['accounts'][number]>[] };
};

export type ValidateChartOfAccountsResult<
  CT extends z.infer<typeof BaseChartOfAccounts>
> = {
  filledIn: ValidatedAccounts<CT['accounts'][number]>;
  errors: {
    message: string;
    path: (string | number)[];
  }[];
};

export const transformAndValidateCoA = <
  CT extends z.infer<typeof BaseChartOfAccounts>
>({
  coa,
}: ValidateChartOfAccountsParams<CT>): ValidateChartOfAccountsResult<CT> => {
  const errors: ValidateChartOfAccountsResult<CT>['errors'] = [];

  // Validate currency settings using the extracted utility
  const currencyValidation = validateDefaultCurrencySettings({
    defaultCurrencyMode: coa.defaultCurrencyMode,
    defaultCurrency: coa.defaultCurrency,
  });

  errors.push(...currencyValidation.errors);

  const defaultCurrencyMode: 'single' | 'multi' =
    coa.defaultCurrencyMode === 'multi' || coa.defaultCurrencyMode === 'single'
      ? coa.defaultCurrencyMode
      : 'single';
  const coaConsistencyConfig = coa.defaultConsistencyConfig ?? {};
  const isTotalBalance = isTotalBalanceSchema(coaConsistencyConfig);

  const rawHasOwnBalance = coaConsistencyConfig.ownBalanceUpdates !== undefined;
  const rawHasTotalBalance =
    coaConsistencyConfig.totalBalanceUpdates !== undefined;

  if (rawHasOwnBalance && rawHasTotalBalance) {
    errors.push({
      message:
        'Schema defaultConsistencyConfig cannot have both ownBalanceUpdates and totalBalanceUpdates set. A schema must use one or the other.',
      path: ['defaultConsistencyConfig'],
    });
  }

  const defaultConsistencyConfig: Omit<
    Required<AccountConsistencyConfig>,
    'groups' | 'totalBalanceUpdates' | 'ownBalanceUpdates'
  > & {
    totalBalanceUpdates?: AccountConsistencyConfig['totalBalanceUpdates'];
    ownBalanceUpdates?: AccountConsistencyConfig['ownBalanceUpdates'];
  } = {
    lines: coaConsistencyConfig.lines ?? 'eventual',
    // Only include ownBalanceUpdates if NOT a totalBalance schema
    ...(!isTotalBalance && {
      ownBalanceUpdates: coaConsistencyConfig.ownBalanceUpdates ?? 'eventual',
    }),
    // Only include totalBalanceUpdates if it's defined
    ...(isTotalBalance && {
      totalBalanceUpdates: coaConsistencyConfig.totalBalanceUpdates,
    }),
  };

  const defaultHasOwnBalance =
    defaultConsistencyConfig.ownBalanceUpdates !== undefined;
  const defaultHasTotalBalance =
    defaultConsistencyConfig.totalBalanceUpdates !== undefined;

  if (!defaultHasOwnBalance && !defaultHasTotalBalance) {
    // We want to push people towards totalBalance, even though ownBalance is technically still supported
    errors.push({
      message:
        'Schema defaultConsistencyConfig is not set. Please set totalBalanceUpdates to either "eventual" or "strong". Visit https://fragment.dev/guides/configure-consistency to learn more.',
      path: ['defaultConsistencyConfig'],
    });
  }

  const { errors: accountErrors, filledIn } = transformAndValidateAccounts({
    defaultConsistencyConfig,
    defaultCurrency: coa.defaultCurrency,
    defaultCurrencyMode,
    accounts: coa.accounts,
  });

  return {
    errors: [...errors, ...accountErrors],
    filledIn,
  };
};
